<template>
  <div>
    <WizardStep v-if="hasEditMerchantSettingsPermission" :hideFooter="true">
      <template v-slot:content>
        <div class="col">
          <!-- Create New Key Section -->
          <div class="col-xl-12 mb-4">
            <div class="card">
              <div class="card-header">
                <h5>Create New API Key</h5>
              </div>
              <div class="card-body">
                <b-form @submit.prevent="createKey">
                  <b-form-group label="Key Name:" label-for="keyName">
                    <b-form-input
                      id="keyName"
                      v-model="newKey.name"
                      placeholder="Enter key name"
                      required
                    ></b-form-input>
                  </b-form-group>

                  <b-form-group label="Scopes:">
                    <div class="row">
                      <div
                        class="col-md-4"
                        v-for="scope in availableScopes"
                        :key="scope"
                      >
                        <b-form-checkbox v-model="newKey.scopes" :value="scope">
                          {{ scope }}
                        </b-form-checkbox>
                      </div>
                    </div>
                  </b-form-group>

                  <b-button type="submit" variant="primary"
                    >Create Key</b-button
                  >
                </b-form>
              </div>
            </div>
          </div>

          <!-- Existing Keys Section -->
          <div class="col-xl-12">
            <div class="card">
              <div class="card-header">
                <h5>Existing API Keys</h5>
              </div>
              <div class="card-body">
                <b-table
                  :items="apiKeys"
                  :fields="tableFields"
                  striped
                  hover
                  responsive
                >
                  <template #cell(scopes)="data">
                    <b-badge
                      v-for="scope in data.item.scopes"
                      :key="scope"
                      variant="info"
                      class="mr-1"
                    >
                      {{ scope }}
                    </b-badge>
                  </template>

                  <template #cell(actions)="data">
                    <b-button
                      size="sm"
                      variant="danger"
                      @click="deleteKey(data.item.id)"
                    >
                      Delete
                    </b-button>
                  </template>
                </b-table>

                <div v-if="!apiKeys.length" class="text-center text-muted">
                  No API keys found
                </div>
              </div>
            </div>
          </div>
        </div>
      </template>
    </WizardStep>

    <LoadingModal v-bind="loadingModal" />
    <AlertModal
      v-bind="alertModal"
      v-model="alertModal.show"
      @dismiss="resetAlert"
    />
    <ConfirmationModal
      v-bind="confirmationModal"
      v-model="confirmationModal.show"
      @dismiss="resetConfirmation"
    />
  </div>
</template>

<script>
import WizardStep from "@/components/wizard/WizardStep";
import MerchantSettingsPermissionsMixin from "@/components/merchantsettings/MerchantSettingsPermissionsMixin.js";
import AlertModalMixin from "@/components/widgets/AlertModalMixin.js";
import { API, graphqlOperation } from "aws-amplify";

const AlertModal = AlertModalMixin.AlertModal;
const ConfirmationModal = AlertModalMixin.ConfirmationModal;

export default {
  mixins: [MerchantSettingsPermissionsMixin, AlertModalMixin],
  components: { WizardStep },
  props: ["heading"],
  data: () => {
    return {
      apiKeys: [],
      newKey: {
        name: "",
        scopes: []
      },
      tableFields: [
        { key: "name", label: "Name" },
        { key: "scopes", label: "Scopes" },
        { key: "actions", label: "Actions" }
      ]
    };
  },
  async mounted() {
    await this.loadKeys();
  },
  computed: {
    availableScopes() {
      return this.$role.API_KEY_SCOPES;
    }
  },
  methods: {
    async loadKeys() {
      if (!this.hasEditMerchantSettingsPermission) return;

      this.startLoadingDialog("Loading API keys");
      try {
        const keys = await this.getApiKeys();
        this.apiKeys = keys || [];
        this.stopLoadingDialog();
      } catch (e) {
        this.$logger.error(e);
        this.stopLoadingDialog("Error loading API keys", AlertModal.ERROR);
      }
    },

    async createKey() {
      if (!this.hasEditMerchantSettingsPermission) return;

      if (!this.newKey.name || !this.newKey.scopes.length) {
        this.alert(
          "Please provide a name and select at least one scope",
          AlertModal.WARNING
        );
        return;
      }

      this.startLoadingDialog("Creating API key");
      try {
        const key = await this.createApiKey(
          this.newKey.name,
          this.newKey.scopes
        );
        this.newKey = { name: "", scopes: [] };
        await this.loadKeys();
        this.stopLoadingDialog(
          this.generateKeyAlertMessage(key),
          AlertModal.SUCCESS,
          true
        );
      } catch (e) {
        this.$logger.error(e);
        this.stopLoadingDialog("Error creating API key", AlertModal.ERROR);
      }
    },
    generateKeyAlertMessage(key) {
      return `
        <div class="text-center">
          <p><strong>API Key Generated Successfully!</strong></p>
          <p class="text-warning"><small>⚠️ This is the only time you'll see this key. Copy it now!</small></p>
          <div class="form-group">
            <input type="text" class="form-control" value="${key}" readonly />
          </div>
          
        </div>
      `;
    },
    async deleteKey(keyId) {
      if (!this.hasEditMerchantSettingsPermission) return;

      const confirm = await this.confirm(
        "Delete API Key",
        "Are you sure you want to delete this API key? This action cannot be undone.",
        ConfirmationModal.DANGER
      );

      if (!confirm) return;

      this.startLoadingDialog("Deleting API key");
      try {
        await this.deleteApiKey(keyId);
        await this.loadKeys();
        this.stopLoadingDialog(
          "API key deleted successfully",
          AlertModal.SUCCESS
        );
      } catch (e) {
        this.$logger.error(e);
        this.stopLoadingDialog("Error deleting API key", AlertModal.ERROR);
      }
    },

    async getApiKeys() {
      if (!this.hasEditMerchantSettingsPermission) return;

      const query = `
        query GetApiKey {
          getApiKey {
            id
            name
            scopes
          }
        }
      `;
      const response = await API.graphql(graphqlOperation(query));
      return response.data.getApiKey;
    },

    async createApiKey(name, scopes) {
      if (!this.hasEditMerchantSettingsPermission) return;

      const mutation = `
        mutation GenerateApiKey($body: MutationGenerateApiKeyInput!) {
          generateApiKey(body: $body)
        }
      `;
      const response = await API.graphql(
        graphqlOperation(mutation, {
          body: { name, scopes }
        })
      );

      const key = response.data.generateApiKey;

      let jsonResponse = undefined;

      try {
        jsonResponse = JSON.parse(key);
      } catch {}

      if (jsonResponse) {
        throw new Error(key);
      }

      return key;
    },

    async deleteApiKey(keyId) {
      if (!this.hasEditMerchantSettingsPermission) return;

      const mutation = `
        mutation DeleteApiKey($body: MutationDeleteApiKeyInput!) {
          deleteApiKey(body: $body){
            success
            message
            statusCode 
          }
        }
      `;

      const response = await API.graphql(
        graphqlOperation(mutation, {
          body: { keyId }
        })
      );

      if (response?.data.deleteApiKey.success) {
        return;
      }

      throw new Error(
        response?.data?.deleteApiKey?.message ||
          response?.data?.deleteApiKey?.statusCode ||
          "Unknown error"
      );
    }
  }
};
</script>

<style lang="scss" scoped>
.card {
  border: 1px solid #e3e6f0;
  border-radius: 0.35rem;
}

.card-header {
  background-color: #f8f9fc;
  border-bottom: 1px solid #e3e6f0;
  padding: 0.75rem 1.25rem;
}

.card-body {
  padding: 1.25rem;
}
</style>
